import { Fragment, useState, useEffect } from "react";
import { Button, MediaQuery, Modal, Table } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useSearchParams } from "react-router-dom";

import { Vehicle, VehicleGroup, FuelType } from "@captiva/shared-types";
import { bulkGenerateAndDownloadQR } from "../../components/shared/bulk-qr-generator";
import { VehicleAssetConfig } from "../../components/listings/configurations/assets/vehicle-asset-config";
import { AssetEditor } from "../../components/shared/asset-editor";
import { FlexOptionsChoiceModal } from "./shared/flex-options-choice-modal";
import { Plus } from "tabler-icons-react";
import { useCreateFlexOptionsSet } from "../../hooks/useCreateFlexOptionsSet";
import TableRowActions from "./shared/table-row-actions";
import { BulkActionsMenu } from "./shared/bulk-actions-menu";

export interface AuthUser {
  uid: string;
  email?: string;
  claims?: {
    companyId?: string;
  };
}

interface VehicleTableProps {
  elements: Vehicle[];
  auth: AuthUser | null;
  reloadCallback: () => void;
  vehicleGroups: VehicleGroup[];
  fuelTypes?: FuelType[];
}

export function VehicleTable({
  elements,
  auth,
  reloadCallback,
  vehicleGroups,
  fuelTypes = [],
}: VehicleTableProps) {
  const newVehicle: Partial<Vehicle> = {
    name: "",
    registrationNumber: "",
    fleetGroupId: "",
    description: "",
    isActive: true,
    useForRebateClaim: true,
    measurementUnit: "KILOMETERS",
    expectedConsumptionRate: 0,
    fuelCapacity: 50,
    lastOdometer: 0,
    fuelTypeId: undefined,
    lastOdometerDate: new Date().toISOString(),
  };
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [editedElement, setEditedElement] = useState<Vehicle | Partial<Vehicle> | undefined>(undefined);
  const [searchParams, setSearchParams] = useSearchParams();
  const editId = searchParams.get("editId");

  useEffect(() => {
    if (editId && elements.length > 0 && !editedElement) {
      const match = elements.find(e => e.id === editId);
      if (match) {
        setEditedElement(match);
      }
    }
  }, [editId, elements, editedElement]);

  const closeEdit = () => {
    setEditedElement(undefined);
    if (searchParams.has("editId")) {
      searchParams.delete("editId");
      setSearchParams(searchParams, { replace: true });
    }
  };

  const rows = elements.map((element) => (
    <Fragment key={element.id}>
      {/* // desktop sizes */}
      <MediaQuery smallerThan="sm" styles={{ display: "none" }}>
        <tr>
          <td>{element.name}</td>
          <td>{element.registrationNumber}</td>
          <td>{element.description}</td>
          <td>
            {vehicleGroups.find((group) => group.id === element.fleetGroupId)?.name ||
              "Ungrouped"}
          </td>
          <td>{String(element.isActive ?? true)}</td>
          <td style={{ textAlign: "left" }}>
            <TableRowActions
              element={element}
              setEditedElement={setEditedElement}
              reloadCallback={reloadCallback}
              assetName="Vehicle"
              collectionName="fleet"
              nameKey="name"
              idKey="id"
            />
          </td>
        </tr>
      </MediaQuery>
      {/* // phone sizes */}
      <MediaQuery largerThan="sm" styles={{ display: "none" }}>
        <tr>
          <td>{element.name}</td>
          <td>{element.registrationNumber}</td>
          <td style={{ textAlign: "left" }}>
            <TableRowActions
              element={element}
              setEditedElement={setEditedElement}
              reloadCallback={reloadCallback}
              assetName="Vehicle"
              collectionName="fleet"
              nameKey="name"
              idKey="id"
            />
          </td>
        </tr>
      </MediaQuery>
    </Fragment>
  ));

  // Bulk QR code generation for all vehicles
  async function handleBulkQRDownload() {
    await bulkGenerateAndDownloadQR({
      assetName: "Vehicle",
      items: elements,
      nameKey: "name",
      idKey: "id",
      zipName: "vehicles-qr-codes.zip",
      logoSrc: "/captiva-mark.png"
    });
  }
  const [createFlexOptionsSet, { loading, notification, clearNotification, existingDocs, mergeOptions, createNewSet }] = useCreateFlexOptionsSet();

  const handleCreateFlexOptionsSet = () => {
    const companyId = auth?.claims?.companyId;
    if (!companyId) {
      alert("No company ID found in auth claims.");
      return;
    }
    createFlexOptionsSet({
      assetType: "Vehicles",
      assetList: elements,
      metadataPrefix: "vehicle",
      fieldMappings: {
        label: "name",
        value: "id",
        metadata: {
          description: "description",
          registrationNumber: "registrationNumber",
          fleetGroupId: "fleetGroupId",
          measurementUnit: "measurementUnit",
          expectedConsumptionRate: "expectedConsumptionRate",
          fuelCapacity: "fuelCapacity",
          name: "name"
        }
      },
      companyId
    });
  };

  return (
    <>
      {notification && notification.type !== "choice" && (
        <div style={{ position: "fixed", top: 76, right: 20, zIndex: 9999 }}>
          <Button color={notification.type === "success" ? "green" : "red"} variant="light" onClick={clearNotification}>
            {notification.message}
          </Button>
        </div>
      )}
      {elements.length > 0 && (
        <Table width="100%">
          <MediaQuery smallerThan="sm" styles={{ display: "none" }}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Registration</th>
                <th>Description</th>
                <th>Group</th>
                <th>Active</th>
                <th colSpan={7} style={{textAlign: 'left'}}>
                  <BulkActionsMenu onBulkQRDownload={handleBulkQRDownload} onCreateFlexOptionsSet={handleCreateFlexOptionsSet} loading={loading} />
                </th>
              </tr>
            </thead>
          </MediaQuery>
          <MediaQuery largerThan="sm" styles={{ display: "none" }}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Registration</th>
                <th>Actions</th>
              </tr>
            </thead>
          </MediaQuery>
          <tbody>{rows}</tbody>
        </Table>
      )}
      {editedElement && (
        <Modal opened onClose={closeEdit} title="Vehicle" {...({ fullScreen: isMobile } as any)}>
          <AssetEditor
            asset={editedElement}
            assetConfig={VehicleAssetConfig}
            collection="fleet"
            auth={auth}
            reloadCallback={() => {
              reloadCallback();
              closeEdit();
            }}
            dependencyList={{
              fleetGroupId: [...vehicleGroups],
              measurementUnit: [
                { id: "KILOMETERS", label: "KILOMETERS" },
                { id: "HOURS", label: "HOURS" },
                { id: "MILES", label: "MILES" },
                { id: "NONE", label: "NONE" },
              ],
              fuelTypeId: fuelTypes.map(ft => ({ id: ft.id, label: ft.name }))
            }}
            dependencyLabel={{ fleetGroupId: "name", measurementUnit: "label", fuelTypeId: "label" }}
          ></AssetEditor>
        </Modal>
      )}
      <div style={{ display: "flex", justifyContent: "center" }}>
        <Button
          onClick={() => setEditedElement(newVehicle)}
          fullWidth
          style={{ maxWidth: 500 }}
          my="xl"
          color="cap-navy"
          variant="outline"
          leftIcon={<Plus />}
        >
          Create new
        </Button>

      </div>
      {notification && notification.type === "choice" && (
        <FlexOptionsChoiceModal
          opened={true}
          onClose={() => clearNotification()}
          onMerge={mergeOptions}
          onCreateNew={createNewSet}
          existingDocs={existingDocs}
          assetType="Vehicles"
        />
      )}
    </>
  );
}
